import { useEffect, useState } from 'react';
import type { EvidenceAsset, Sth } from '../lib/evidence.ts';
import type { DemoAsset } from '../lib/demo-evidence.ts';
import { Button, Cell, Empty, Label, Row, Section, Status, Table, type Tone } from '../ui.tsx';

/**
 * A lightweight "recent evidence activity" surface.
 *
 * It is built entirely from what the Evidence screen already reads — the asset
 * list and the transparency-log head — turned into a reverse-chronological
 * feed. There is **no push pipeline** behind it and it deliberately reaches
 * across no service boundary: it is a *view* of the same list, not a new
 * channel.
 *
 * The one optional behaviour is an auto-refresh toggle, and the copy is honest
 * about what it is: it re-polls this panel while the page is open. It is not
 * email, SMS or push, and it does not claim to be — nothing here notifies
 * anybody who is not already looking at the screen.
 */

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ActivityItem {
  at: string;
  label: string;
  tone: Tone;
  detail: string;
  assetId?: string;
  demo?: boolean;
}

function buildFeed(
  assets: EvidenceAsset[],
  demo: DemoAsset[],
  sth: Sth | null | undefined,
): ActivityItem[] {
  const items: ActivityItem[] = [];

  for (const a of assets) {
    if (a.committed_at) {
      items.push({
        at: a.committed_at,
        label: 'Evidence committed',
        tone: 'safe',
        detail: a.media_type,
        assetId: a.id,
      });
    } else {
      items.push({
        at: a.created_at,
        label: 'Evidence ingested',
        tone: 'neutral',
        detail: a.media_type,
        assetId: a.id,
      });
    }
  }

  for (const d of demo) {
    items.push({
      at: d.captured_at,
      label: 'Sample record added',
      tone: 'neutral',
      detail: d.media_type,
      demo: true,
    });
  }

  if (sth) {
    items.push({
      at: sth.signed_at,
      label: 'Transparency log head signed',
      tone: 'neutral',
      detail: `${sth.tree_size} entries`,
    });
  }

  return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 8);
}

export function EvidenceActivity({
  assets,
  demoAssets = [],
  sth,
  onRefresh,
  onOpenAsset,
}: {
  assets: EvidenceAsset[];
  demoAssets?: DemoAsset[];
  sth?: Sth | null;
  onRefresh?: () => void;
  onOpenAsset?: (id: string) => void;
}) {
  const [auto, setAuto] = useState(false);
  const feed = buildFeed(assets, demoAssets, sth);

  useEffect(() => {
    if (!auto || !onRefresh) return;
    const t = setInterval(onRefresh, 15_000);
    return () => clearInterval(t);
  }, [auto, onRefresh]);

  return (
    <Section
      title="Recent activity"
      action={
        onRefresh ? (
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink-2)]">
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
              className="h-4 w-4"
            />
            Auto-refresh
          </label>
        ) : null
      }
    >
      <p className="mb-3 max-w-prose text-[13px] text-[var(--ink-3)]">
        A view of what this workspace holds, most recent first — drawn from the evidence list
        itself.
        {onRefresh
          ? ' Auto-refresh re-polls this panel while the page is open; it is an in-app view, not email or push.'
          : ''}
      </p>

      {feed.length === 0 ? (
        <Empty>
          Nothing has happened here yet. Ingested evidence and transparency-log activity appear here
          as they occur.
        </Empty>
      ) : (
        <Table head={['When', 'Activity', 'Detail', '']}>
          {feed.map((item, i) => (
            <Row key={i}>
              <Cell>{formatWhen(item.at)}</Cell>
              <Cell wide>
                <span className="inline-flex items-center gap-2">
                  <Status tone={item.tone}>{item.label}</Status>
                  {item.demo ? (
                    <span className="rounded-[var(--radius-1)] border border-[var(--line-2)] px-1.5 py-0.5 text-[11px] font-medium tracking-[0.04em] text-[var(--ink-2)] uppercase">
                      Sample
                    </span>
                  ) : null}
                </span>
              </Cell>
              <Cell>
                {item.demo ? (
                  <span className="text-[var(--ink-3)]">
                    <Label>Not in the store</Label>
                  </span>
                ) : (
                  item.detail
                )}
              </Cell>
              <Cell>
                {item.assetId && onOpenAsset ? (
                  <Button
                    variant="quiet"
                    label={`Open evidence from ${formatWhen(item.at)}`}
                    onClick={() => onOpenAsset(item.assetId!)}
                  >
                    Open
                  </Button>
                ) : null}
              </Cell>
            </Row>
          ))}
        </Table>
      )}
    </Section>
  );
}
